import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createHouseVisit,
  updateHouseVisit,
  getHouseVisit,
  deleteHouseVisit,
  getOrders,
} from "../../api/server";
import {
  flattenObjectWithPefix,
  unFlattenObjectWithPefix,
} from "../../components/util/helpers";
import { withNotifications } from "../notifications/notificationsSlice";
import { notifyError } from "../notifications/notificationsSlice";
import moment from "moment";
const initialState = {
  error: null,
  activeHouseVisit: {},
  changedItems: {
    changed: [],
    values: {},
  },
};

// Load data for the house visits. Combine two API calls...
// house visit events, and patient orders.
export const loadRecord = createAsyncThunk(
  "visit/load",
  async (params, thunkAPI) => {
    const {error, data} = await getOrders(params);
    if (error) {
      return async() => thunkAPI.dispatch(notifyError({title: "get orders", message: error}));
    }
    const orderData = data;

    // Cheap client-side filtering of orders before current date
    // until we understand server-side filtering.
    orderData.items = orderData.items.filter(item => {
      if (item.effective_to === null) {
        return true;
      }
      return new Date(item.effective_to) >= new Date();
    });
    return getHouseVisit(params).then(({ error, data }) => {
      if (error) {
        thunkAPI.dispatch(
          notifyError({ title: "Get house visit", message: error })
        );
      }
      data.orders = orderData.items;
      return data;
    });
  }
);

export const commitChanges = createAsyncThunk(
  "visit/update",
  withNotifications(
    async (params, thunkAPI) => {
      const { visit } = thunkAPI.getState();
      const { activeHouseVisit } = visit;
      const branchId = activeHouseVisit.patient__branch;
      const id = activeHouseVisit.patient__id;

      const {
        changedItems: { changed, values },
      } = visit;
      const toUpdate = changed.reduce((acc, key) => {
        acc[key] = values[key] === undefined ? null : values[key];
        return acc;
      }, {});
      const finalValues = unFlattenObjectWithPefix(toUpdate);
      const date = finalValues.date || activeHouseVisit.date;
      return updateHouseVisit({
        house_visit: {
          id: activeHouseVisit.id,
          ...finalValues,
          date: moment(date).format(),
          verified:
            finalValues.approved === false &&
            (finalValues.verified || activeHouseVisit.verified)
              ? false
              : finalValues.verified,
        },
        patientId: id,
        branchId,
      });
    },
    "save_house_visit",
    "save_house_visit_success",
    "save_house_visit_error"
  )
);

export const createRecord = createAsyncThunk(
  "visit/create",
  withNotifications(
    async (params, thunkAPI) => {
      const { visit } = thunkAPI.getState();
      const { activeHouseVisit } = visit;
      const branchId = activeHouseVisit.patient__branch;
      const id = activeHouseVisit.patient__id;
      const {
        changedItems: { changed, values },
      } = visit;
      const toUpdate = changed.reduce((acc, key) => {
        acc[key] = values[key] === undefined ? null : values[key];
        return acc;
      }, {});
      return createHouseVisit({
        house_visit: {
          id: activeHouseVisit.id,
          ...unFlattenObjectWithPefix(toUpdate),
        },
        patientId: id,
        branchId,
      });
    },
    "create_house_visit",
    "create_house_visit_success",
    "create_house_visit_error"
  )
);

export const deleteRecord = createAsyncThunk(
  "visit/delete",
  withNotifications(
    deleteHouseVisit,
    "delete_house_visit",
    "delete_house_visit_success",
    "delete_house_visit_error"
  )
);

const visitSlice = createSlice({
  name: "visit",
  initialState: initialState,
  reducers: {
    setActiveHouseVisit(state, action) {
      const { record } = action.payload;
      state.activeHouseVisit = flattenObjectWithPefix(record);
    },
    setChangedFormItems(state, action) {
      const { changed, values } = action.payload;
      state.changedItems.changed = changed;
      state.changedItems.values = values;
    },
    reset(state, action) {
      state.error = null;
      state.activeHouseVisit = {};
      state.changedItems = initialState.changedItems;
    },
  },
  extraReducers: {
    [loadRecord.fulfilled]: (state, action) => {
      state.activeHouseVisit = flattenObjectWithPefix(action.payload);
    },
    [loadRecord.rejected]: (state, action) => {
      state.error = action.error;
    },
    [commitChanges.fulfilled]: (state, { payload: { error, data } }) => {
      state.changedItems = { changed: [], values: {} };
      state.error = error;
    },
    [commitChanges.rejected]: (state, action) => {
      state.error = action.error;
    },
    [createRecord.fulfilled]: (state, { payload: { error, data } }) => {
      state.changedItems = { changed: [], values: {} };
      state.error = error;
    },
    [createRecord.rejected]: (state, action) => {
      state.error = action.error;
    },
    [deleteRecord.fulfilled]: (state, action) => {
      state.activeHouseVisit = {};
    },
    [deleteRecord.rejected]: (state, action) => {
      state.error = action.error;
    },
  },
});

export const { reset, setActiveHouseVisit, setChangedFormItems } =
  visitSlice.actions;

export default visitSlice.reducer;
